// --- React
import React from "react";
import { Navigate } from "react-router-dom";
// --- Hooks
import { usePermissions } from "../../utils/permissions";

/**
 * ProtectedRoute component that controls access based on user permissions.
 *
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - The protected content to render.
 * @param {boolean} [props.requireAdmin=false] - Requires admin permission.
 * @param {boolean} [props.requireTapAdminOrDigitalStores=false] - Requires TAP admin OR digital stores permission.
 * @param {boolean} [props.requireJobTracker=false] - Requires job tracker permission.
 * @param {boolean} [props.blockDigitalStores=false] - Blocks digital stores users.
 * @param {string} [props.redirectTo="/home"] - Path to redirect unauthorized users.
 * @returns {JSX.Element} The protected content or redirect.
 */
export function ProtectedRoute({
  children,
  requireAdmin = false,
  requireTapAdminOrDigitalStores = false,
  requireJobTracker = false,
  blockDigitalStores = false,
  redirectTo = "/home",
}) {
  const { isAdmin, isTapAdmin, isDigitalStoresUser, isJobTracker } =
    usePermissions();

  // Check admin-only routes
  if (requireAdmin && !isAdmin) {
    return <Navigate to={redirectTo} replace />;
  }

  // Check TAP admin or Digital Stores routes (e.g., TPR Exceptions)
  if (requireTapAdminOrDigitalStores && !isTapAdmin && !isDigitalStoresUser) {
    return <Navigate to={redirectTo} replace />;
  }

  // Check Job Tracker routes (e.g., TAP Jobs, PMX Deliveries)
  if (requireJobTracker && !isJobTracker) {
    return <Navigate to={redirectTo} replace />;
  }

  // Block digital stores users from certain routes
  if (blockDigitalStores && isDigitalStoresUser) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
}
